// Org chart team block: colour band, description, lead(s) on top, people with faces + other hats, team feed.
import { useState } from "react";
import { toast } from "sonner";
import { Crown, Users, Pencil, MessageSquarePlus, Lock, Palmtree, ChevronDown } from "lucide-react";
import SquadAvatar from "../../../components/squad/SquadAvatar";
import { workspaceApi, teamColor } from "../_shared";

function Person({ node, lead, color, otherTeams, faces, onOpen }) {
  const f = faces[node.id] || faces[node.name] || {};
  const ooo = node.out_of_office;
  return (
    <button onClick={() => onOpen(node.id)} data-testid={`ws-org-person-${node.id}`}
      className={`w-full flex items-center gap-2.5 rounded-lg px-2 py-1.5 text-left border transition-colors ${ooo ? "border-rose-500/40 bg-rose-500/[0.06]" : lead ? "border-transparent bg-white/[0.05]" : "border-transparent hover:bg-white/[0.05]"}`}
      style={lead ? { borderColor: `${color}55` } : undefined}>
      <SquadAvatar name={node.name} url={f.avatar_url} status={f.status} size={lead ? 34 : 28} />
      <div className="min-w-0 flex-1">
        <div className={`truncate flex items-center gap-1 ${lead ? "text-[13px] font-semibold text-white" : "text-[12px] text-slate-200"}`}>
          {node.name}{lead && <Crown className="w-3 h-3 text-amber-300 shrink-0" />}
        </div>
        <div className="text-[10px] font-mono text-slate-500 truncate">
          {ooo ? <span className="text-rose-300 inline-flex items-center gap-1"><Palmtree className="w-3 h-3" />{ooo.reason || "Out"}</span> : lead ? "Team lead" : (node.job_title || node.role_workspace || node.role || "Member")}
        </div>
      </div>
      {otherTeams.length > 0 && (
        <div className="flex -space-x-1 shrink-0" title={`Also in: ${otherTeams.map((t) => t.name).join(", ")}`} data-testid={`ws-org-hats-${node.id}`}>
          {otherTeams.slice(0, 4).map((t) => <span key={t.id} className="w-2.5 h-2.5 rounded-full ring-2 ring-[#0F1420]" style={{ background: teamColor(t) }} />)}
          <span className="text-[9.5px] font-mono text-slate-500 pl-2">+{otherTeams.length}</span>
        </div>
      )}
    </button>
  );
}

export default function OrgTeamCard({ team, teams, nodeById, faces, canEdit, canLead, onOpen, onEdit, onSaved }) {
  const [feed, setFeed] = useState(false);
  const [text, setText] = useState("");
  const c = teamColor(team);
  const leadIds = new Set(team.manager_ids || []);
  const members = (team.member_ids || []).map((id) => nodeById[id]).filter(Boolean);
  const leads = members.filter((m) => leadIds.has(m.id));
  const staff = members.filter((m) => !leadIds.has(m.id));
  const others = (id) => teams.filter((t) => t.id !== team.id && (t.member_ids || []).includes(id));
  const post = async () => {
    if (!text.trim()) return;
    try { await workspaceApi.addTeamComment(team.id, text.trim()); setText(""); toast.success("Posted to team"); onSaved?.(); }
    catch (e) { toast.error("Comment failed", { description: e?.response?.data?.detail || "" }); }
  };
  return (
    <div className="rounded-xl border overflow-hidden bg-[#0F1420]/60 flex flex-col" style={{ borderColor: `${c}40` }} data-testid={`ws-org-team-${team.id}`}>
      <div className="px-4 pt-3.5 pb-3" style={{ background: `linear-gradient(135deg, ${c}2e, ${c}08 70%)`, borderBottom: `1px solid ${c}30` }}>
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full" style={{ background: c, boxShadow: `0 0 12px ${c}` }} />
          <div className="text-[15px] font-semibold text-white flex-1 truncate">{team.name}</div>
          {team.visibility === "private" && <Lock className="w-3.5 h-3.5 text-amber-300" title="Private team" />}
          <span className="text-[10px] font-mono text-slate-300 inline-flex items-center gap-1"><Users className="w-3 h-3" />{members.length}</span>
          {canEdit && <button onClick={() => onEdit(team)} className="p-1 rounded hover:bg-white/10 text-slate-300 hover:text-white" title="Edit team" data-testid={`ws-org-team-edit-${team.id}`}><Pencil className="w-3.5 h-3.5" /></button>}
        </div>
        <p className="text-[11.5px] text-slate-300 mt-1.5 line-clamp-3" data-testid={`ws-org-team-desc-${team.id}`}>{team.description || <span className="text-slate-500 italic">No description yet.</span>}</p>
      </div>
      <div className="p-2.5 space-y-1 flex-1">
        {leads.length > 0 ? leads.map((m) => <Person key={m.id} node={m} lead color={c} otherTeams={others(m.id)} faces={faces} onOpen={onOpen} />)
          : <div className="text-[10.5px] font-mono text-slate-500 px-2 py-1" data-testid={`ws-org-no-lead-${team.id}`}>No team lead set{canLead ? " — edit to add one" : ""}</div>}
        {staff.length > 0 && <div className="border-t border-white/5 my-1.5" />}
        {staff.map((m) => <Person key={m.id} node={m} color={c} otherTeams={others(m.id)} faces={faces} onOpen={onOpen} />)}
        {members.length === 0 && <div className="text-[11px] text-slate-500 italic px-2">No members yet.</div>}
      </div>
      <div className="border-t border-white/5 px-3 py-2">
        <button onClick={() => setFeed((v) => !v)} className="text-[10px] font-mono uppercase tracking-widest text-slate-500 hover:text-white inline-flex items-center gap-1" data-testid={`ws-org-team-feed-${team.id}`}>
          Team notes · {(team.comments || []).length}<ChevronDown className={`w-3 h-3 transition-transform ${feed ? "rotate-180" : ""}`} />
        </button>
        {feed && (
          <div className="mt-2 space-y-1.5">
            {(team.comments || []).slice().reverse().map((x) => (
              <div key={x.id} className="text-[11px] text-slate-300"><span className="text-slate-500 font-mono text-[10px]">{x.by_name || x.by_email || x.by} · </span>{x.text}</div>))}
            {canEdit && <div className="flex items-center gap-2">
              <input className="ws-input flex-1" placeholder="Post a note to the team…" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && post()} data-testid={`ws-org-team-comment-input-${team.id}`} />
              <button onClick={post} disabled={!text.trim()} className="px-2 py-1 rounded border border-teal-500/40 bg-teal-500/10 text-teal-200 text-[10px] font-mono uppercase disabled:opacity-50" data-testid={`ws-org-team-comment-post-${team.id}`}><MessageSquarePlus className="w-3 h-3" /></button>
            </div>}
          </div>
        )}
      </div>
    </div>
  );
}
